<template>
    <div class="glass-container">
        <div class="user-profile-container">
            <!-- Back Navigation (commented out) -->
            <!-- <div class="profile-header">
                <b-button style="color: white;" @click="$router.go(-1)" variant="outline-secondary" class="back-btn">
                    <b-icon style="color: white;"icon="arrow-left" class="mr-2"></b-icon>
                    Back
                </b-button>
                <h1 class="page-title">User Sales Profile</h1>
            </div> -->

            <!-- Loading State -->
            <div v-if="loading" class="loading-container">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <div class="loading-text">Loading profile data...</div>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="error-container">
                <b-alert show variant="danger">
                    <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
                    {{ error }}
                </b-alert>
                <b-button @click="loadProfileData" variant="primary">
                    <b-icon icon="arrow-clockwise" class="mr-2"></b-icon>
                    Retry
                </b-button>
            </div>

            <!-- Profile Content -->
            <div v-else-if="profileData" class="profile-content">
                <!-- Date Range Selector -->
                <div class="date-range-selector mb-4">
                    <b-card class="date-selector-card">
                        <div class="date-controls">
                            <div class="date-picker-section">
                                <div class="section-label">Select Date Range</div>
                                <date-picker
                                    v-model="date_range"
                                    format="MM-DD-YYYY"
                                    valueType="format"
                                    style="width: 250px; font-size: 14px;"
                                    range
                                    placeholder="Select date range"
                                ></date-picker>
                                <b-button 
                                    @click="fetchWithDates" 
                                    variant="primary" 
                                    class="ml-2"
                                    :disabled="!date_range || date_range.length === 0"
                                >
                                    <b-icon icon="search" class="mr-1"></b-icon>
                                    Apply Dates
                                </b-button>
                                <b-button 
                                    @click="clearDates" 
                                    variant="outline-secondary" 
                                    class="ml-2"
                                >
                                    <b-icon icon="x" class="mr-1"></b-icon>
                                    Clear
                                </b-button>
                            </div>
                            
                            <div class="quick-date-buttons">
                                <div class="section-label">Quick Date Ranges</div>
                                <b-button-group class="quick-buttons">
                                    <b-button 
                                        size="sm" 
                                        variant="outline-primary"
                                        :pressed="picked_date_range === 'today'"
                                        @click="setDates('today')"
                                    >
                                        Today
                                    </b-button>
                                    <b-button 
                                        size="sm" 
                                        variant="outline-primary"
                                        :pressed="picked_date_range === 'yesterday'"
                                        @click="setDates('yesterday')"
                                    >
                                        Yesterday
                                    </b-button>
                                    <b-button 
                                        size="sm" 
                                        variant="outline-primary"
                                        :pressed="picked_date_range === 'this_week'"
                                        @click="setDates('this_week')"
                                    >
                                        This Week
                                    </b-button>
                                    <b-button 
                                        size="sm" 
                                        variant="outline-primary"
                                        :pressed="picked_date_range === 'last_week'"
                                        @click="setDates('last_week')"
                                    >
                                        Last Week
                                    </b-button>
                                    <b-button 
                                        size="sm" 
                                        variant="outline-primary"
                                        :pressed="picked_date_range === 'this_month'"
                                        @click="setDates('this_month')"
                                    >
                                        This Month
                                    </b-button>
                                    <b-button 
                                        size="sm" 
                                        variant="outline-primary"
                                        :pressed="picked_date_range === 'last_month'"
                                        @click="setDates('last_month')"
                                    >
                                        Last Month
                                    </b-button>
                                </b-button-group>
                            </div>
                        </div>
                        
                        <div v-if="date_range && date_range.length > 0" class="date-display mt-2">
                            <small class="text-muted">
                                Showing data for: 
                                <strong>{{ formatDisplayDate(date_range[0]) }}</strong>
                                <span v-if="date_range[1] && date_range[1] !== date_range[0]">
                                    to <strong>{{ formatDisplayDate(date_range[1]) }}</strong>
                                </span>
                            </small>
                        </div>
                    </b-card>
                </div>

                <!-- User Header -->
                <div class="user-profile-header">
                    <div class="user-avatar-section">
                        <div class="avatar-wrapper">
                            <img
                                v-if="profileData.user_info.image_url"
                                :src="getUserImage(profileData.user_info)"
                                :alt="profileData.user_info.fullname"
                                class="profile-avatar"
                                @error="handleImageError"
                            />
                            <div v-else class="avatar-placeholder">
                                {{ getInitials(profileData.user_info.fullname || profileData.user_info.username) }}
                            </div>
                        </div>
                    </div>

                    <div class="user-info-section">
                        <h2 class="user-name">{{ profileData.user_info.fullname || profileData.user_info.username }}</h2>
                        <div class="user-details">
                            <span class="user-username">@{{ profileData.user_info.username }}</span>
                            <span class="user-role" :class="getRoleClass(profileData.user_info.userrole)">
                                {{ getUserRoleLabel(profileData.user_info.userrole) }}
                            </span>
                            <span class="user-status" :class="{ 'active': profileData.user_info.active }">
                                <b-icon :icon="profileData.user_info.active ? 'check-circle' : 'x-circle'"></b-icon>
                                {{ profileData.user_info.active ? 'Active' : 'Inactive' }}
                            </span>
                        </div>
                        
                        <!-- Stats based on selected date range -->
                        <div class="user-stats-overview">
                            <div class="stat-item">
                                <div class="stat-value">{{ formatCurrency(currentPeriodStats.total_revenue || 0) }}</div>
                                <div class="stat-label">Period Revenue</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-value">{{ currentPeriodStats.total_sales || 0 }}</div>
                                <div class="stat-label">Period Sales</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-value">{{ formatCurrency(currentPeriodStats.total_commission || 0) }}</div>
                                <div class="stat-label">Period Commission</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-value">{{ formatCurrency(currentPeriodStats.avg_sale_amount || 0) }}</div>
                                <div class="stat-label">Avg Sale</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Time Period Comparison -->
                <div class="period-selector mb-4">
                    <b-card class="period-card">
                        <div class="comparison-header">
                            <h6 class="mb-3">Compare Periods</h6>
                        </div>
                        <b-button-group>
                            <b-button 
                                @click="activePeriod = 'date_stats'" 
                                :variant="activePeriod === 'date_stats' ? 'primary' : 'outline-primary'"
                            >
                                Selected Dates
                            </b-button>
                            <b-button 
                                @click="activePeriod = 'last_7_days_stats'" 
                                :variant="activePeriod === 'last_7_days_stats' ? 'primary' : 'outline-primary'"
                            >
                                Last 7 Days
                            </b-button>
                            <b-button 
                                @click="activePeriod = 'last_30_days_stats'" 
                                :variant="activePeriod === 'last_30_days_stats' ? 'primary' : 'outline-primary'"
                            >
                                Last 30 Days
                            </b-button>
                            <b-button 
                                @click="activePeriod = 'lifetime_stats'" 
                                :variant="activePeriod === 'lifetime_stats' ? 'primary' : 'outline-primary'"
                            >
                                Lifetime
                            </b-button>
                        </b-button-group>
                    </b-card>
                </div>

                <!-- Stats Grid -->
                <div class="stats-grid">
                    <!-- Revenue Card -->
                    <b-card class="stat-card revenue-card">
                        <div class="card-header">
                            <b-icon icon="cash-stack" class="card-icon"></b-icon>
                            <h5 class="card-title">Revenue</h5>
                        </div>
                        <div class="card-content">
                            <div class="stat-main-value">
                                {{ formatCurrency(currentStats.total_revenue || 0) }}
                            </div>
                            <div class="stat-sub-value">
                                {{ currentStats.total_sales || 0 }} Sales
                            </div>
                            <div class="stat-change" v-if="showComparison && comparisonStats">
                                <b-icon 
                                    :icon="getChangeIcon(revenueChange)" 
                                    :class="getChangeClass(revenueChange)"
                                ></b-icon>
                                {{ formatPercentage(revenueChange) }} vs {{ comparisonPeriodLabel }}
                            </div>
                        </div>
                    </b-card>

                    <!-- Commission Card -->
                    <b-card class="stat-card commission-card">
                        <div class="card-header">
                            <b-icon icon="percent" class="card-icon"></b-icon>
                            <h5 class="card-title">Commission</h5>
                        </div>
                        <div class="card-content">
                            <div class="stat-main-value">
                                {{ formatCurrency(currentStats.total_commission || 0) }}
                            </div>
                            <div class="stat-sub-value">
                                {{ formatCurrency(currentStats.avg_sale_amount || 0) }} avg sale
                            </div>
                            <div class="commission-breakdown">
                                <div class="breakdown-item">
                                    <span class="breakdown-label">Sales:</span>
                                    <span class="breakdown-value">{{ formatCurrency(currentStats.total_commission || 0) }}</span>
                                </div>
                                <div class="breakdown-item">
                                    <span class="breakdown-label">Delivery:</span>
                                    <span class="breakdown-value">{{ formatCurrency(currentStats.delivery_commission || 0) }}</span>
                                </div>
                                <div class="breakdown-item">
                                    <span class="breakdown-label">Setup:</span>
                                    <span class="breakdown-value">{{ formatCurrency(currentStats.setup_commission || 0) }}</span>
                                </div>
                                <div class="breakdown-item">
                                    <span class="breakdown-label">Warranty:</span>
                                    <span class="breakdown-value">{{ formatCurrency(currentStats.warranty_commission || 0) }}</span>
                                </div>
                            </div>
                        </div>
                    </b-card>

                    <!-- Services Card -->
                    <b-card class="stat-card services-card">
                        <div class="card-header">
                            <b-icon icon="tools" class="card-icon"></b-icon>
                            <h5 class="card-title">Services Sold</h5>
                        </div>
                        <div class="card-content">
                            <div class="services-grid">
                                <div class="service-item">
                                    <div class="service-icon">
                                        <b-icon icon="truck"></b-icon>
                                    </div>
                                    <div class="service-details">
                                        <div class="service-count">{{ currentStats.deliveries_sold || 0 }}</div>
                                        <div class="service-label">Deliveries</div>
                                        <div class="service-value">{{ formatCurrency(currentStats.total_delivery || 0) }}</div>
                                    </div>
                                </div>
                                <div class="service-item">
                                    <div class="service-icon">
                                        <b-icon icon="wrench"></b-icon>
                                    </div>
                                    <div class="service-details">
                                        <div class="service-count">{{ currentStats.setups_sold || 0 }}</div>
                                        <div class="service-label">Setups</div>
                                        <div class="service-value">{{ formatCurrency(currentStats.total_setup || 0) }}</div>
                                    </div>
                                </div>
                                <div class="service-item">
                                    <div class="service-icon">
                                        <b-icon icon="shield-check"></b-icon>
                                    </div>
                                    <div class="service-details">
                                        <div class="service-count">{{ currentStats.warranties_sold || 0 }}</div>
                                        <div class="service-label">Warranties</div>
                                        <div class="service-value">{{ formatCurrency(currentStats.total_warranty || 0) }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-card>

                    <!-- Performance Card -->
                    <b-card class="stat-card performance-card">
                        <div class="card-header">
                            <b-icon icon="graph-up" class="card-icon"></b-icon>
                            <h5 class="card-title">Performance</h5>
                        </div>
                        <div class="card-content">
                            <div class="performance-metrics">
                                <div class="metric-item">
                                    <div class="metric-label">Avg Daily Sales</div>
                                    <div class="metric-value">{{ profileData.performance_metrics.avg_daily_sales || 0 | number('0.0') }}</div>
                                </div>
                                <div class="metric-item">
                                    <div class="metric-label">Avg Daily Revenue</div>
                                    <div class="metric-value">{{ formatCurrency(profileData.performance_metrics.avg_daily_revenue || 0) }}</div>
                                </div>
                                <div class="metric-item">
                                    <div class="metric-label">Active Days</div>
                                    <div class="metric-value">{{ profileData.performance_metrics.active_days || 0 }}</div>
                                </div>
                                <div class="metric-item">
                                    <div class="metric-label">Rank</div>
                                    <div class="metric-value">
                                        #{{ profileData.performance_metrics.revenue_rank || 0 }} of {{ profileData.performance_metrics.total_salespeople || 0 }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-card>
                </div>

                <!-- Charts Section -->
                <div class="charts-section">
                    <!-- Monthly Trend Chart -->
                    <b-card class="chart-card">
                        <div class="card-header">
                            <h5 class="card-title">Monthly Sales Trend</h5>
                        </div>
                        <div class="card-body">
                            <div v-if="profileData.monthly_trend && profileData.monthly_trend.length > 0">
                                <canvas ref="monthlyChart" id="monthlyChartCanvas"></canvas>
                            </div>
                            <div v-else class="no-data">
                                <b-icon icon="bar-chart" font-scale="3"></b-icon>
                                <div class="no-data-text">No monthly data available</div>
                            </div>
                        </div>
                    </b-card>

                    <!-- Payment Method Breakdown -->
                    <b-card class="chart-card">
                        <div class="card-header">
                            <h5 class="card-title">Payment Method Breakdown</h5>
                        </div>
                        <div class="card-body">
                            <div v-if="profileData.payment_breakdown && profileData.payment_breakdown.length > 0">
                                <canvas ref="paymentChart" id="paymentChartCanvas"></canvas>
                            </div>
                            <div v-else class="no-data">
                                <b-icon icon="pie-chart" font-scale="3"></b-icon>
                                <div class="no-data-text">No payment data available</div>
                            </div>
                        </div>
                    </b-card>
                </div>

                <!-- Top Products -->
                <div class="top-products-section">
                    <b-card class="products-card">
                        <div class="card-header">
                            <h5 class="card-title">Top Selling Products</h5>
                            <small class="text-muted">For selected date range</small>
                        </div>
                        <div class="card-body">
                            <div v-if="profileData.top_products && profileData.top_products.length > 0">
                                <b-table
                                    :items="profileData.top_products"
                                    :fields="productFields"
                                    striped
                                    hover
                                    small
                                >
                                    <template #cell(product_name)="data">
                                        <div class="product-cell">
                                            <div class="product-name">{{ data.value }}</div>
                                            <small class="text-muted">{{ data.item.category_name }}</small>
                                        </div>
                                    </template>
                                    <template #cell(total_quantity_sold)="data">
                                        {{ data.value }}
                                    </template>
                                    <template #cell(total_revenue)="data">
                                        {{ formatCurrency(data.value) }}
                                    </template>
                                    <template #cell(avg_selling_price)="data">
                                        {{ formatCurrency(data.value) }}
                                    </template>
                                </b-table>
                            </div>
                            <div v-else class="no-data">
                                <b-icon icon="box" font-scale="3"></b-icon>
                                <div class="no-data-text">No product sales data available for selected period</div>
                            </div>
                        </div>
                    </b-card>
                </div>

                <!-- Recent Activity - Box Layout -->
                <div class="recent-activity-section">
                    <b-card class="activity-card">
                        <div class="card-header">
                            <h5 class="card-title">Recent Activity</h5>
                            <small class="text-muted">Showing {{ profileData.recent_activity ? profileData.recent_activity.length : 0 }} invoices for selected date range</small>
                        </div>
                        <div class="card-body">
                            <div v-if="profileData.recent_activity && profileData.recent_activity.length > 0">
                                <!-- Invoice Grid - 4 boxes per row -->
                                <div class="invoice-grid">
                                    <div v-for="(invoice, index) in profileData.recent_activity" :key="index" class="invoice-box">
                                        <div class="invoice-header">
                                            <div class="invoice-id">#{{ invoice.invoice_id }}</div>
                                            <div class="invoice-date">{{ formatShortDate(invoice.transaction_date) }}</div>
                                        </div>
                                        
                                        <div class="invoice-amount">
                                            {{ formatCurrency(invoice.total) }}
                                        </div>
                                        
                                        <div class="invoice-details">
                                            <div class="detail-row">
                                                <span class="detail-label">Status:</span>
                                                <span class="detail-value" :class="{ 'text-success': invoice.completed, 'text-warning': !invoice.completed }">
                                                    {{ invoice.completed ? 'Delivered' : 'Written' }}
                                                </span>
                                            </div>
                                            
                                            <div class="detail-row">
                                                <span class="detail-label">Type:</span>
                                                <span class="detail-value" :class="{ 'text-info': invoice.was_layaway }">
                                                    {{ invoice.was_layaway ? 'Layaway' : 'Sale' }}
                                                </span>
                                            </div>
                                            
                                            <div class="detail-row">
                                                <span class="detail-label">Paid:</span>
                                                <span class="detail-value">
                                                    {{ formatCurrency(invoice.paid_amount || 0) }}
                                                </span>
                                            </div>
                                            
                                            <div v-if="invoice.payment_methods" class="detail-row payment-methods">
                                                <span class="detail-label">Payment:</span>
                                                <span class="detail-value">
                                                    {{ invoice.payment_methods }}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div class="invoice-footer">
                                            <div class="commission-badge" v-if="invoice.commission">
                                                <b-icon icon="percent"></b-icon>
                                                {{ formatCurrency(invoice.commission) }}
                                            </div>
                                            
                                            <div class="services-badges">
                                                <span v-if="invoice.delivery_charges > 0" class="badge badge-delivery">
                                                    <b-icon icon="truck"></b-icon>
                                                </span>
                                                <span v-if="invoice.setup_charges > 0" class="badge badge-setup">
                                                    <b-icon icon="wrench"></b-icon>
                                                </span>
                                                <span v-if="invoice.warranty_charges > 0" class="badge badge-warranty">
                                                    <b-icon icon="shield-check"></b-icon>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Show count of invoices -->
                                <div class="invoice-count mt-3 text-center">
                                    <small class="text-muted">
                                        Showing {{ profileData.recent_activity.length }} invoices
                                    </small>
                                </div>
                            </div>
                            <div v-else class="no-data">
                                <b-icon icon="clock-history" font-scale="3"></b-icon>
                                <div class="no-data-text">No recent activity for selected period</div>
                            </div>
                        </div>
                    </b-card>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Requestor from '@/utilities/Requestor'
import Chart from 'chart.js'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import moment from 'moment'

export default {
    name: 'UserProfile',
    components: {
        DatePicker
    },
    data() {
        return {
            loading: true,
            error: null,
            profileData: null,
            date_range: [],
            picked_date_range: null,
            activePeriod: 'date_stats', // Default to date_stats when dates are selected
            productFields: [
                { key: 'product_name', label: 'Product', sortable: true },
                { key: 'category_name', label: 'Category', sortable: true },
                { key: 'total_quantity_sold', label: 'Qty Sold', sortable: true },
                { key: 'total_revenue', label: 'Revenue', sortable: true },
                { key: 'times_sold', label: 'Times Sold', sortable: true },
                { key: 'avg_selling_price', label: 'Avg Price', sortable: true }
            ],
            monthlyChart: null,
            paymentChart: null
        }
    },
    computed: {
        currentPeriodStats() {
            if (!this.profileData) return {}
            // Use date_stats when date range is selected, otherwise use default
            if (this.date_range && this.date_range.length > 0 && this.profileData.date_stats) {
                return this.profileData.date_stats
            }
            return this.profileData.last_30_days_stats || {}
        },
        currentStats() {
            if (!this.profileData) return {}
            const key = this.activePeriod
            return this.profileData[key] || {}
        },
        comparisonStats() {
            if (!this.profileData) return null
            // Compare with last 30 days when viewing date_stats
            if (this.activePeriod === 'date_stats' && this.profileData.last_30_days_stats) {
                return this.profileData.last_30_days_stats
            }
            // Compare with lifetime when viewing 30_days
            if (this.activePeriod === 'last_30_days_stats' && this.profileData.lifetime_stats) {
                return this.profileData.lifetime_stats
            }
            return null
        },
        comparisonPeriodLabel() {
            if (this.activePeriod === 'date_stats') return 'Last 30 Days'
            if (this.activePeriod === 'last_30_days_stats') return 'Lifetime'
            return ''
        },
        revenueChange() {
            const current = this.currentStats.total_revenue || 0
            const comparison = this.comparisonStats ? (this.comparisonStats.total_revenue || 0) : 0
            
            if (comparison === 0) return 0
            return ((current - comparison) / comparison) * 100
        },
        showComparison() {
            return this.activePeriod === 'date_stats' || this.activePeriod === 'last_30_days_stats'
        }
    },
    methods: {
        loadProfileData() {
            this.loading = true
            this.error = null
            
            const username = this.$route.params.username
            const path = '/api/reports/reports_user_profile/'
            const data = {
                username: this.packet.userDetails.username,
                target_user: username,
                dates: this.date_range
            }
            
            const success = (response) => {
                if (response.data.error) {
                    this.error = response.data.error
                } else {
                    this.profileData = response.data.result
                    this.$nextTick(() => {
                        this.renderCharts()
                    })
                }
                this.loading = false
            }
            
            const failure = (error) => {
                console.error('Error loading profile:', error)
                this.error = 'Failed to load profile data. Please try again.'
                this.loading = false
            }
            
            Requestor.postSyncP(path, data, this, success, failure)
        },
        
        fetchWithDates() {
            if (!this.date_range || this.date_range.length === 0) {
                this.$bvToast.toast(
                    "Please select a date range",
                    {title: "Warning", autoHideDelay: 2000, variant:"warning"}
                )
                return
            }
            
            this.loadProfileData()
        },
        
        setDates(value) {
            let start = null
            let end = null

            if(value === 'today') {
                start = moment().format("MM-DD-YYYY")
                end = moment().format("MM-DD-YYYY")
            }
            if(value === 'yesterday') {
                start = moment().add(-1, 'days').format("MM-DD-YYYY")
                end = moment().add(-1, 'days').format("MM-DD-YYYY")
            }
            if(value === 'this_week') {
                start = moment().startOf('week').format("MM-DD-YYYY")
                end = moment().endOf('week').format("MM-DD-YYYY")
            }
            if(value === 'last_week') {
                start = moment().subtract(1, 'weeks').startOf('week').format("MM-DD-YYYY")
                end = moment().subtract(1, 'weeks').endOf('week').format("MM-DD-YYYY")
            }
            if(value === 'this_month') {
                start = moment().startOf('month').format("MM-DD-YYYY")
                end = moment().endOf('month').format("MM-DD-YYYY")
            }
            if(value === 'last_month') {
                start = moment().subtract(1, 'months').startOf('month').format("MM-DD-YYYY")
                end = moment().subtract(1, 'months').endOf('month').format("MM-DD-YYYY")
            }

            if (start && end) {
                this.date_range = [start, end]
                this.picked_date_range = value
                this.activePeriod = 'date_stats' // Switch to date stats view
                this.fetchWithDates()
            }
        },
        
        clearDates() {
            this.date_range = []
            this.picked_date_range = null
            this.activePeriod = 'last_30_days_stats' // Switch back to 30 days
            this.loadProfileData()
        },
        
        formatDisplayDate(dateStr) {
            if (!dateStr) return ''
            try {
                // Parse MM-DD-YYYY format
                const parts = dateStr.split('-')
                if (parts.length === 3) {
                    const date = new Date(parts[2], parts[0] - 1, parts[1])
                    return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    })
                }
                return dateStr
            } catch (error) {
                return dateStr
            }
        },
        
        formatShortDate(dateString) {
            if (!dateString) return 'N/A'
            try {
                const date = new Date(dateString)
                return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                })
            } catch (error) {
                return dateString
            }
        },
        
        formatDate(dateString) {
            if (!dateString) return 'N/A'
            try {
                const date = new Date(dateString)
                return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                })
            } catch (error) {
                return dateString
            }
        },
        
        formatCurrency(value) {
            if (!value && value !== 0) return '$0.00'
            const num = parseFloat(value)
            if (isNaN(num)) return '$0.00'
            return '$' + num.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
        },
        
        formatNumber(value) {
            if (!value && value !== 0) return '0'
            const num = parseFloat(value)
            if (isNaN(num)) return '0'
            
            if (num >= 1000000) {
                return (num / 1000000).toFixed(1) + 'M'
            } else if (num >= 1000) {
                return (num / 1000).toFixed(1) + 'K'
            }
            
            return num.toLocaleString('en-US')
        },
        
        formatPercentage(value) {
            if (!value && value !== 0) return '0%'
            const num = parseFloat(value)
            if (isNaN(num)) return '0%'
            
            const sign = num >= 0 ? '+' : ''
            return sign + num.toFixed(1) + '%'
        },
        
        getChangeIcon(change) {
            return change >= 0 ? 'arrow-up' : 'arrow-down'
        },
        
        getChangeClass(change) {
            return change >= 0 ? 'text-success' : 'text-danger'
        },
        
        getUserImage(user) {
            if (!user || !user.image_url) return null
            
            let imageUrl = user.image_url
            if (typeof imageUrl === 'string') {
                imageUrl = imageUrl.trim()
            }
            
            if (!imageUrl || imageUrl === '' || imageUrl === 'null' || imageUrl === 'undefined') {
                return null
            }
            
            if (imageUrl.includes('fmapi.myfurnituremecca.com')) {
                return 'https://corsproxy.io/?' + encodeURIComponent(imageUrl)
            }
            
            return imageUrl
        },
        
        handleImageError(event) {
            event.target.style.display = 'none'
        },
        
        getInitials(name) {
            if (!name) return '?'
            return name.split(' ')
                .map(word => word[0])
                .join('')
                .toUpperCase()
                .substring(0, 2)
        },
        
        getRoleClass(role) {
            const roleClasses = {
                'admin': 'role-admin',
                'salesperson': 'role-sales',
                'locationadmin': 'role-location',
                'officeuser': 'role-office',
                'accountmanager': 'role-account',
                'default': 'role-default'
            }
            return roleClasses[role] || roleClasses.default
        },
        
        getUserRoleLabel(role) {
            const roleLabels = {
                'admin': 'Admin',
                'salesperson': 'Sales Person',
                'locationadmin': 'Location Admin',
                'officeuser': 'Office User',
                'accountmanager': 'Account Manager',
                'locationuser': 'Location User',
                'warehouseuser': 'Warehouse User'
            }
            return roleLabels[role] || role
        },
        
        getActivityIcon(activity) {
            if (activity.was_layaway) {
                return activity.completed ? 'check-circle' : 'clock'
            }
            return activity.completed ? 'truck' : 'file-earmark-text'
        },
        
        renderCharts() {
            // Destroy existing charts
            if (this.monthlyChart) {
                this.monthlyChart.destroy()
            }
            if (this.paymentChart) {
                this.paymentChart.destroy()
            }
            
            // Render Monthly Trend Chart
            if (this.profileData.monthly_trend && this.profileData.monthly_trend.length > 0) {
                const ctx = this.$refs.monthlyChart
                if (ctx) {
                    this.monthlyChart = new Chart(ctx, {
                        type: 'bar',
                        data: {
                            labels: this.profileData.monthly_trend.map(m => m.month_name),
                            datasets: [{
                                label: 'Revenue',
                                data: this.profileData.monthly_trend.map(m => m.total_revenue || 0),
                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                borderColor: 'rgba(255, 255, 255, 0.8)',
                                borderWidth: 2,
                                borderRadius: 8,
                                borderSkipped: false,
                            }]
                        },
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    grid: {
                                        color: 'rgba(255, 255, 255, 0.1)'
                                    },
                                    ticks: {
                                        color: 'rgba(255, 255, 255, 0.8)',
                                        callback: (value) => '$' + this.formatNumber(value)
                                    }
                                },
                                x: {
                                    grid: {
                                        color: 'rgba(255, 255, 255, 0.1)'
                                    },
                                    ticks: {
                                        color: 'rgba(255, 255, 255, 0.8)'
                                    }
                                }
                            },
                            plugins: {
                                legend: {
                                    display: false
                                },
                                tooltip: {
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    titleColor: 'rgba(255, 255, 255, 0.9)',
                                    bodyColor: 'rgba(255, 255, 255, 0.8)',
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                    borderWidth: 1,
                                    callbacks: {
                                        label: (context) => {
                                            return `$${this.formatNumber(context.raw)}`
                                        }
                                    }
                                }
                            }
                        }
                    })
                }
            }
            
            // Render Payment Method Chart
            if (this.profileData.payment_breakdown && this.profileData.payment_breakdown.length > 0) {
                const ctx = this.$refs.paymentChart
                if (ctx) {
                    this.paymentChart = new Chart(ctx, {
                        type: 'doughnut',
                        data: {
                            labels: this.profileData.payment_breakdown.map(p => p.payment_method),
                            datasets: [{
                                data: this.profileData.payment_breakdown.map(p => p.amount || 0),
                                backgroundColor: [
                                    'rgba(102, 126, 234, 0.8)',
                                    'rgba(118, 75, 162, 0.8)',
                                    'rgba(245, 101, 101, 0.8)',
                                    'rgba(72, 187, 120, 0.8)',
                                    'rgba(237, 137, 54, 0.8)',
                                    'rgba(66, 153, 225, 0.8)'
                                ],
                                borderColor: [
                                    'rgba(255, 255, 255, 0.3)',
                                    'rgba(255, 255, 255, 0.3)',
                                    'rgba(255, 255, 255, 0.3)',
                                    'rgba(255, 255, 255, 0.3)',
                                    'rgba(255, 255, 255, 0.3)',
                                    'rgba(255, 255, 255, 0.3)'
                                ],
                                borderWidth: 1,
                                borderRadius: 8
                            }]
                        },
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: {
                                    position: 'right',
                                    labels: {
                                        color: 'rgba(255, 255, 255, 0.8)',
                                        padding: 20,
                                        font: {
                                            size: 12
                                        }
                                    }
                                },
                                tooltip: {
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    titleColor: 'rgba(255, 255, 255, 0.9)',
                                    bodyColor: 'rgba(255, 255, 255, 0.8)',
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                    borderWidth: 1,
                                    callbacks: {
                                        label: (context) => {
                                            const label = context.label || ''
                                            const value = context.raw || 0
                                            const total = this.profileData.payment_breakdown.reduce((sum, p) => sum + (p.amount || 0), 0)
                                            const percentage = total > 0 ? Math.round((value / total) * 100) : 0
                                            return `${label}: $${this.formatNumber(value)} (${percentage}%)`
                                        }
                                    }
                                }
                            },
                            cutout: '60%'
                        }
                    })
                }
            }
        }
    },
    watch: {
        activePeriod() {
            this.$nextTick(() => {
                this.renderCharts()
            })
        }
    },
    mounted() {
        // Set default to last 30 days for more data
        const startDate = moment().subtract(30, 'days').format("MM-DD-YYYY")
        const endDate = moment().format("MM-DD-YYYY")
        this.date_range = [startDate, endDate]
        this.picked_date_range = null
        this.activePeriod = 'date_stats'
        this.loadProfileData()
    },
    beforeDestroy() {
        // Clean up charts
        if (this.monthlyChart) {
            this.monthlyChart.destroy()
        }
        if (this.paymentChart) {
            this.paymentChart.destroy()
        }
    },
    created() {
        this.packet = JSON.parse(localStorage.getItem('packet')) || {}
        this.packet.userDetails = JSON.parse(localStorage.getItem('userDetails')) || {}
    }
}
</script>

<style scoped>
/* Glass Morphism Base with Background Image */
.glass-container {
    min-height: 100vh;
    padding: 20px;
    background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

.glass-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(2px);
    z-index: 0;
}

.user-profile-container {
    position: relative;
    z-index: 1;
}

/* Loading State */
.loading-container {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(15px);
    border-radius: 20px;
    padding: 60px 20px;
    text-align: center;
    box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.4);
    margin: 30px 0;
}

.loading-text {
    margin-top: 20px;
    color: #364050;
    font-weight: 600;
}

/* Error State */
.error-container {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(15px);
    border-radius: 20px;
    padding: 40px 20px;
    text-align: center;
    box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.4);
    margin: 30px 0;
}

.error-container .alert {
    background: rgba(245, 101, 101, 0.15);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(245, 101, 101, 0.3);
    color: #c53030;
    border-radius: 10px;
}

/* Profile Content */
.profile-content {
    background: transparent;
    backdrop-filter: blur(20px);
    border-radius: 20px;
    padding: 30px;
    box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.4);
    margin-top: 30px;
    position: relative;
    overflow: hidden;
}

.profile-content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
        radial-gradient(circle at 25% 25%, rgba(102, 126, 234, 0.03) 0%, transparent 50%),
        radial-gradient(circle at 75% 75%, rgba(118, 75, 162, 0.03) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
}

.profile-content > * {
    position: relative;
    z-index: 1;
}

/* Date Range Selector */
.date-selector-card {
    background: rgba(255, 255, 255, 0.8) !important;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.5) !important;
    border-radius: 15px !important;
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
    position: relative;
    overflow: hidden;
}

.date-selector-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
        linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, transparent 100%);
    pointer-events: none;
}

.section-label {
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 5px;
    text-transform: uppercase;
}

.date-display {
    padding: 8px 12px;
    background: rgba(248, 250, 252, 0.9);
    backdrop-filter: blur(5px);
    border-radius: 8px;
    border-left: 4px solid rgba(102, 126, 234, 0.4);
    font-size: 14px;
    color: #4a5568;
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.date-display strong {
    color: #2d3748;
}

/* User Header */
.user-profile-header {
    display: flex;
    align-items: center;
    padding-bottom: 30px;
    margin-bottom: 30px;
    border-bottom: 2px solid rgba(237, 242, 247, 0.8);
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(15px);
    border-radius: 15px;
    padding: 25px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
    position: relative;
    overflow: hidden;
}

.user-profile-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
        linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, transparent 50%),
        linear-gradient(225deg, rgba(118, 75, 162, 0.03) 0%, transparent 50%);
    pointer-events: none;
}

.user-avatar-section {
    margin-right: 30px;
    position: relative;
    z-index: 1;
}

.avatar-wrapper {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    overflow: hidden;
    border: 4px solid rgba(255, 255, 255, 0.8);
    box-shadow: 
        0 8px 32px rgba(102, 126, 234, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.9),
        0 0 0 1px rgba(102, 126, 234, 0.1);
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    position: relative;
}

.avatar-wrapper::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, #364050, #364050);
    border-radius: 50%;
    z-index: -1;
    opacity: 0.3;
}

.profile-avatar {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.avatar-placeholder {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #364050 0%, #364050 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 2.5rem;
    font-weight: 600;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.user-info-section {
    flex: 1;
    position: relative;
    z-index: 1;
}

.user-name {
    font-size: 2rem;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 10px;
    text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

.user-details {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.user-username {
    color: #718096;
    font-size: 1.1rem;
}

.user-role {
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(226, 232, 240, 0.8);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.role-admin {
    background: rgba(245, 101, 101, 0.15);
    color: #c53030;
    border-color: rgba(245, 101, 101, 0.3);
}

.role-sales {
    background: rgba(72, 187, 120, 0.15);
    color: #276749;
    border-color: rgba(72, 187, 120, 0.3);
}

.role-location {
    background: rgba(66, 153, 225, 0.15);
    color: #2c5282;
    border-color: rgba(66, 153, 225, 0.3);
}

.role-office {
    background: rgba(159, 122, 234, 0.15);
    color: #553c9a;
    border-color: rgba(159, 122, 234, 0.3);
}

.role-account {
    background: rgba(237, 137, 54, 0.15);
    color: #c05621;
    border-color: rgba(237, 137, 54, 0.3);
}

.role-default {
    background: rgba(160, 174, 192, 0.15);
    color: #4a5568;
    border-color: rgba(160, 174, 192, 0.3);
}

.user-status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    border-radius: 12px;
    font-size: 0.9rem;
    font-weight: 500;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(226, 232, 240, 0.8);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.user-status.active {
    background: rgba(72, 187, 120, 0.15);
    color: #276749;
    border-color: rgba(72, 187, 120, 0.3);
}

.user-status:not(.active) {
    background: rgba(245, 101, 101, 0.15);
    color: #c53030;
    border-color: rgba(245, 101, 101, 0.3);
}

/* User Stats Overview */
.user-stats-overview {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-top: 20px;
}

.stat-item {
    text-align: center;
    padding: 20px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    transition: all 0.3s ease;
    cursor: default;
    position: relative;
    overflow: hidden;
}

.stat-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #364050, #364050);
    opacity: 0.7;
}

.stat-item:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 1);
    box-shadow: 
        0 12px 32px rgba(0, 0, 0, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.stat-value {
    font-size: 1.8rem;
    font-weight: 700;
    color: #364050;
    margin-bottom: 8px;
    text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

.stat-label {
    font-size: 0.9rem;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Period Selector */
.period-card {
    background: rgba(255, 255, 255, 0.85) !important;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    border-radius: 15px !important;
    box-shadow: 
        0 4px 20px rgba(0, 0, 0, 0.05),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.comparison-header h6 {
    color: #2d3748;
    font-weight: 600;
}

/* Stats Grid */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
}

/* Stats Cards */
.stat-card {
    background: rgba(255, 255, 255, 0.85) !important;
    backdrop-filter: blur(15px);
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    border-radius: 15px !important;
    box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.stat-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #364050,#364050);
    opacity: 0.5;
}

.stat-card:hover {
    background: rgba(255, 255, 255, 0.95) !important;
    transform: translateY(-5px);
    box-shadow: 
        0 16px 40px rgba(0, 0, 0, 0.12),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

/* Card Headers */
.card-header {
    background: linear-gradient(
        135deg,
        rgba(248, 250, 252, 0.95),
        rgba(255, 255, 255, 0.85)
    ) !important;
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(226, 232, 240, 0.8) !important;
    border-radius: 15px 15px 0 0 !important;
    padding: 15px 20px;
}

.card-icon {
    font-size: 1.5rem;
    margin-right: 10px;
    color:#364050;
}

.card-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #2d3748;
}

.card-content {
    padding: 20px;
}

.stat-main-value {
    font-size: 2rem;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 5px;
}

.stat-sub-value {
    font-size: 1rem;
    color: #718096;
    margin-bottom: 15px;
}

.stat-change {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.9rem;
    padding: 8px 12px;
    background: rgba(248, 250, 252, 0.9);
    backdrop-filter: blur(5px);
    border-radius: 8px;
    border: 1px solid rgba(226, 232, 240, 0.8);
    color: #4a5568;
}

.text-success {
    color: #48bb78 !important;
}

.text-danger {
    color: #f56565 !important;
}

/* Commission Breakdown */
.commission-breakdown {
    margin-top: 15px;
}

.breakdown-item {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px dashed rgba(226, 232, 240, 0.8);
}

.breakdown-item:last-child {
    border-bottom: none;
}

.breakdown-label {
    color: #718096;
    font-size: 0.9rem;
}

.breakdown-value {
    font-weight: 600;
    color: #2d3748;
}

/* Services Grid */
.services-grid {
    display: grid;
    gap: 15px;
}

.service-item {
    display: flex;
    align-items: center;
    padding: 15px;
    background: rgba(248, 250, 252, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 10px;
    border: 1px solid rgba(226, 232, 240, 0.8);
    position: relative;
    overflow: hidden;
}

.service-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(to bottom,#364050, #364050);
    opacity: 0.5;
}

.service-icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: rgba(102, 126, 234, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 15px;
    color: #364050;
    font-size: 1.5rem;
    border: 1px solid rgba(102, 126, 234, 0.3);
    position: relative;
    z-index: 1;
}

.service-details {
    flex: 1;
}

.service-count {
    font-size: 1.2rem;
    font-weight: 700;
    color: #2d3748;
}

.service-label {
    font-size: 0.9rem;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.service-value {
    font-size: 0.9rem;
    color:#364050;
    font-weight: 600;
}

/* Performance Metrics */
.performance-metrics {
    display: grid;
    gap: 15px;
}

.metric-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: rgba(248, 250, 252, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 8px;
    border: 1px solid rgba(226, 232, 240, 0.8);
    position: relative;
    overflow: hidden;
}

.metric-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(to bottom, #364050, #364050);
    opacity: 0.3;
}

.metric-label {
    color: #718096;
    font-size: 0.9rem;
}

.metric-value {
    font-weight: 700;
    color: #2d3748;
}

/* Charts Section */
.charts-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
}

/* Chart Cards */
.chart-card {
    background: rgba(255, 255, 255, 0.85) !important;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    border-radius: 15px !important;
    box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
    height: 400px;
    position: relative;
    overflow: hidden;
}

.chart-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg,#364050,#364050);
    opacity: 0.5;
}

.chart-card .card-body {
    height: 350px;
    padding: 20px;
    background: transparent;
}

.no-data {
    text-align: center;
    padding: 40px 20px;
    color: #a0aec0;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.no-data-text {
    margin-top: 15px;
    font-size: 1rem;
    color: #718096;
}

/* Top Products */
.products-card {
    background: rgba(255, 255, 255, 0.85) !important;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    border-radius: 15px !important;
    box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
    position: relative;
    overflow: hidden;
}

.products-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg,#364050, #364050);
    opacity: 0.5;
}

.product-cell {
    display: flex;
    flex-direction: column;
}

.product-name {
    font-weight: 600;
    color: #2d3748;
}

/* Recent Activity - Box Layout */
.activity-card {
    background: rgba(255, 255, 255, 0.85) !important;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    border-radius: 15px !important;
    box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
    position: relative;
    overflow: hidden;
}

.activity-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg,#364050,#364050);
    opacity: 0.5;
}

/* Invoice Grid - 4 boxes per row */
.invoice-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 15px;
    margin-bottom: 20px;
}

.invoice-box {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    border: 1px solid rgba(226, 232, 240, 0.8);
    padding: 15px;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.invoice-box:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
    border-color: rgba(102, 126, 234, 0.3);
}

.invoice-box::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    opacity: 0.8;
}

.invoice-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px dashed rgba(226, 232, 240, 0.8);
}

.invoice-id {
    font-weight: 700;
    color: #2d3748;
    font-size: 1rem;
}

.invoice-date {
    font-size: 0.8rem;
    color: #718096;
}

.invoice-amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: #364050;
    text-align: center;
    margin: 10px 0;
    padding: 10px 0;
    border-radius: 8px;
    background: rgba(248, 250, 252, 0.8);
}

.invoice-details {
    flex: 1;
    margin-bottom: 15px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
    font-size: 0.85rem;
}

.detail-label {
    color: #718096;
    font-weight: 500;
}

.detail-value {
    font-weight: 600;
    color: #2d3748;
}

.text-success {
    color: #48bb78 !important;
}

.text-warning {
    color: #ed8936 !important;
}

.text-info {
    color: #4299e1 !important;
}

.payment-methods {
    font-size: 0.8rem;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed rgba(226, 232, 240, 0.5);
}

.invoice-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 10px;
    border-top: 1px solid rgba(226, 232, 240, 0.8);
}

.commission-badge {
    background: rgba(72, 187, 120, 0.15);
    color: #276749;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 5px;
    border: 1px solid rgba(72, 187, 120, 0.3);
}

.services-badges {
    display: flex;
    gap: 5px;
}

.badge {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    border: 1px solid transparent;
}

.badge-delivery {
    background: rgba(102, 126, 234, 0.15);
    color: #667eea;
    border-color: rgba(102, 126, 234, 0.3);
}

.badge-setup {
    background: rgba(237, 137, 54, 0.15);
    color: #ed8936;
    border-color: rgba(237, 137, 54, 0.3);
}

.badge-warranty {
    background: rgba(159, 122, 234, 0.15);
    color: #9f7aea;
    border-color: rgba(159, 122, 234, 0.3);
}

.invoice-count {
    background: rgba(248, 250, 252, 0.9);
    backdrop-filter: blur(5px);
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid rgba(226, 232, 240, 0.8);
}

/* Buttons */
.btn-primary {
    background: linear-gradient(135deg,#364050 0%,#364050 100%) !important;
    border: none !important;
    border-radius: 8px;
    padding: 8px 16px;
    font-weight: 600;
    transition: all 0.3s ease;
    color: white;
    position: relative;
    overflow: hidden;
}

.btn-primary::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: 0.5s;
}

.btn-primary:hover::before {
    left: 100%;
}

.btn-primary:hover {
    background: linear-gradient(135deg, #5a6fd8 0%, #69438f 100%) !important;
    transform: translateY(-2px);
    box-shadow: 
        0 8px 25px rgba(102, 126, 234, 0.3),
        0 4px 12px rgba(102, 126, 234, 0.2);
}

.btn-primary:disabled {
    background: linear-gradient(135deg, #c3cfe2 0%, #a8b4d0 100%) !important;
    border: none !important;
    color: rgba(255, 255, 255, 0.7);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

.btn-outline-primary {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid#364050 !important;
    border-radius: 8px;
    color:#364050;
    transition: all 0.3s ease;
    backdrop-filter: blur(5px);
}

.btn-outline-primary:hover {
    background: rgba(102, 126, 234, 0.15) !important;
    border-color: #364050!important;
    color: #364050;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.btn-outline-primary.active {
    background: rgba(102, 126, 234, 0.25) !important;
    border-color: #364050 !important;
    color: #364050;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.btn-outline-secondary {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid #cbd5e0 !important;
    border-radius: 8px;
    color: #718096;
    transition: all 0.3s ease;
    backdrop-filter: blur(5px);
}

.btn-outline-secondary:hover {
    background: rgba(203, 213, 224, 0.15) !important;
    border-color: #a0aec0 !important;
    color: #4a5568;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(160, 174, 192, 0.15);
}

/* Quick Date Buttons */
.quick-buttons .btn {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid #e2e8f0 !important;
    color: #4a5568;
    transition: all 0.3s ease;
    backdrop-filter: blur(5px);
}

.quick-buttons .btn:hover {
    background: rgba(248, 250, 252, 0.95) !important;
    color: #2d3748;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.quick-buttons .btn:focus,
.quick-buttons .btn.active {
    background: rgba(102, 126, 234, 0.15) !important;
    border-color: #364050 !important;
    color: #364050;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

/* Table */
.table {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 10px;
    overflow: hidden;
    backdrop-filter: blur(5px);
    border: 1px solid rgba(226, 232, 240, 0.8);
}

.table th {
    background: rgba(248, 250, 252, 0.95) !important;
    border-color: #e2e8f0 !important;
    color: #2d3748;
    font-weight: 600;
    backdrop-filter: blur(5px);
}

.table td {
    border-color: #e2e8f0 !important;
    color: #4a5568;
}

.table-striped tbody tr:nth-of-type(odd) {
    background-color: rgba(248, 250, 252, 0.6);
}

.table-hover tbody tr:hover {
    background-color: rgba(102, 126, 234, 0.08) !important;
}

/* Toast Styles */
.toast {
    border-radius: 10px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(226, 232, 240, 0.9);
    backdrop-filter: blur(10px);
}

/* Animation for loading */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.profile-content {
    animation: fadeIn 0.5s ease-out;
}

/* Custom scrollbar */
::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

::-webkit-scrollbar-track {
    background: rgba(226, 232, 240, 0.5);
    border-radius: 4px;
    backdrop-filter: blur(5px);
}

::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg,#364050 0%,#364050 100%);
    border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg,#364050 0%,#364050 100%);
}

/* Responsive Styles */
@media (max-width: 1200px) {
    .charts-section {
        grid-template-columns: 1fr;
    }
    
    .invoice-grid {
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    }
}

@media (max-width: 992px) {
    .invoice-grid {
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    }
}

@media (max-width: 768px) {
    .glass-container {
        padding: 10px;
    }
    
    .profile-content {
        padding: 20px;
    }
    
    .user-profile-header {
        flex-direction: column;
        text-align: center;
    }
    
    .user-avatar-section {
        margin-right: 0;
        margin-bottom: 20px;
    }
    
    .user-details {
        justify-content: center;
    }
    
    .stats-grid {
        grid-template-columns: 1fr;
    }
    
    .charts-section {
        grid-template-columns: 1fr;
    }
    
    .chart-card {
        height: 350px;
    }
    
    .chart-card .card-body {
        height: 300px;
    }
    
    .date-controls {
        flex-direction: column;
    }
    
    .date-picker-section,
    .quick-date-buttons {
        min-width: 100%;
    }
    
    .quick-buttons {
        justify-content: center;
    }
    
    .period-selector .btn-group {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
    }
    
    .period-selector .btn {
        flex: 1;
        min-width: 120px;
        margin: 2px;
    }
    
    .user-stats-overview {
        grid-template-columns: repeat(2, 1fr);
    }
    
    .invoice-grid {
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    }
}

@media (max-width: 480px) {
    .user-stats-overview {
        grid-template-columns: 1fr;
    }
    
    .page-title {
        font-size: 1.5rem;
    }
    
    .user-name {
        font-size: 1.5rem;
    }
    
    .stat-main-value {
        font-size: 1.5rem;
    }
    
    .chart-card {
        height: 300px;
    }
    
    .chart-card .card-body {
        height: 250px;
        padding: 15px;
    }
    
    .charts-section {
        grid-template-columns: 1fr;
    }
    
    .invoice-grid {
        grid-template-columns: 1fr;
    }
    
    .invoice-box {
        min-height: 200px;
    }
}

/* Print styles */
@media print {
    .glass-container {
        background: white !important;
    }
    
    .glass-container::before {
        display: none;
    }
    
    .date-range-selector,
    .period-selector,
    .btn {
        display: none !important;
    }
    
    .profile-content {
        box-shadow: none !important;
        margin-top: 0 !important;
        background: white !important;
        color: black !important;
        backdrop-filter: none !important;
    }
    
    .stat-card {
        break-inside: avoid;
        background: white !important;
        color: black !important;
        backdrop-filter: none !important;
    }
    
    .invoice-grid {
        grid-template-columns: repeat(2, 1fr) !important;
        gap: 10px !important;
    }
    
    .user-name,
    .stat-value,
    .card-title {
        color: black !important;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/importables/UserProfile.vue